import { CdkDrag, CdkDragDrop, CdkDragEnd, CdkDragStart, CdkDropList, copyArrayItem, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PageEmptySearchResultComponent } from "../../../common-shared-module/components/page-empty-search-result/page-empty-search-result.component";
import { RowLayoutMetadata } from '../../../common-shared-module/model/dyn-form/form-metadata/layout/row-layout.metadata.model';
import { CreatedLayout } from '../../models/created-layout-model';
import { CreatedRow } from '../../models/created-row.model';
import { CreatedWidget } from '../../models/created-widget.model';
import { DDElement } from '../../models/dd-element.model';
import { Layout } from '../../models/layout.model';
import { MovedLayout } from '../../models/moved-layout.model';
import { MovedRow } from '../../models/moved-row.model';
import { MovedWidget } from '../../models/moved-widget.model';
import { RemovedRow } from '../../models/removed-row.model';
import { RemovedWidget } from '../../models/removed-widget.model';
import { RepeatLayout } from '../../models/repeat-layout.model';
import { RequestedAddTool } from '../../models/requested-add-tool.model';
import { RowLayout } from '../../models/row-layout.model';
import { SelectedLayout } from '../../models/selected-layout.model';
import { SelectedRow } from '../../models/selected-row.model';
import { SelectedWidget } from '../../models/selected-widget.model';
import { TableLayout } from '../../models/table-layout.model';
import { TransferredWidget } from '../../models/transferred-widget.model';
import { DdSupportService } from '../../services/dd-support/dd-support.service';
import { WebServiceLayout } from '../../models/webservice-layout.model';

@Component({
    selector: 'app-form-editor-section-layout',
    templateUrl: './form-editor-section-layout.component.html',
    styleUrl: './form-editor-section-layout.component.scss',
    standalone: true,
    imports: [
    CommonModule, DragDropModule,
    MatIconModule, MatMenuModule, MatButtonModule,
    PageEmptySearchResultComponent
]
})
export class FormEditorSectionLayoutComponent {

    @Output() movedRow: EventEmitter<MovedRow> = new EventEmitter()
    @Output() movedLayout: EventEmitter<MovedLayout> = new EventEmitter()
    @Output() removedRow: EventEmitter<RemovedRow> = new EventEmitter()
    @Output() createdRow: EventEmitter<CreatedRow> = new EventEmitter()
    @Output() createdLayout: EventEmitter<CreatedLayout> = new EventEmitter()
    @Output() removedWidget: EventEmitter<RemovedWidget> = new EventEmitter()
    @Output() movedWidget: EventEmitter<MovedWidget> = new EventEmitter()
    @Output() createdWidget: EventEmitter<CreatedWidget> = new EventEmitter()
    @Output() transferredWidget: EventEmitter<TransferredWidget> = new EventEmitter()
    @Output() selectedWidget: EventEmitter<SelectedWidget> = new EventEmitter()
    @Output() selectedRow: EventEmitter<SelectedRow> = new EventEmitter()
    @Output() selectedLayout: EventEmitter<SelectedLayout> = new EventEmitter()
    @Output() selectedSection: EventEmitter<boolean> = new EventEmitter()
    @Output() requestAddTool: EventEmitter<RequestedAddTool> = new EventEmitter()
    @Output() recordOnHistory: EventEmitter<boolean> = new EventEmitter()

    ddWidget: boolean = false
    ddRow: boolean = false
    ddRowLayout: boolean = false
    hoverRowLayout: Layout | null = null
    currentRows: RowLayout[] = []
    maxLines: number = 20


    constructor(public ddService: DdSupportService, protected snack: MatSnackBar) { }


    clearState() {
        this.currentRows = []
    }

    availableLines(): number {
        return this.maxLines - this.currentRows.length
    }

    getLayoutList(e: RowLayout): DDElement[] {
        return e.widgets
    }

    startDDWidget($event: CdkDragStart, widget: DDElement) {
        this.ddWidget = !this.ddWidget
        this.ddService.ddElement = widget
    }

    endDDWidget($event: CdkDragEnd) {
        this.ddWidget = !this.ddWidget
        this.ddService.ddElement = null
    }

    startDDRow($event: CdkDragStart) {
        this.ddRow = !this.ddRow
    }

    endDDrow($event: CdkDragEnd) {
        this.ddRow = !this.ddRow
    }

    removeRow(currentRow: RowLayout) {
        currentRow.widgets.forEach(p => p.removeDependencies())
        this.currentRows = this.currentRows.filter(r => r != currentRow)
        this.removedRow.emit({ row: currentRow })
        currentRow.destroy()
    }

    addRowWithMetadata(index: number, afterRow: null | RowLayout, layout: Layout | null, metadata: RowLayoutMetadata, fromBottom: boolean = true): CreatedRow {
        let theRow = new RowLayout(metadata)
        return this.addRowIntern(layout, afterRow, theRow, fromBottom, index);
    }

    addRow(index: number, afterRow: null | RowLayout, layout: Layout | null, fromBottom: boolean = true): CreatedRow {
        let theRow = new RowLayout()
        return this.addRowIntern(layout, afterRow, theRow, fromBottom, index);
    }

    private addRowIntern(layout: Layout | null, afterRow: RowLayout | null, theRow: RowLayout, fromBottom: boolean, index: number) {
        if (layout != null && afterRow != null) {
            theRow.setLayoutAndAfter(layout, afterRow, fromBottom);
        }
        this.currentRows.splice(index, 0, theRow);
        let result = { row: theRow, index: index, layout: layout };
        this.createdRow.emit(result);
        return result;
    }

    addLayoutRow(index: number, layout: Layout): CreatedLayout {
        let theRow = new RowLayout()

        layout.rows.push(theRow)
        theRow.layout = layout
        this.currentRows.splice(index, 0, theRow)
        let result = { layout: layout, index: index, row: theRow }
        this.createdLayout.emit(result)
        return result
    }

    dropCreateRowLayout(event: CdkDragDrop<RowLayout>, index: number, afterRow: null | RowLayout, layout: Layout | null) {
        if (event.item.data == null || event.item.data.layout == false) {
            let createdRow = this.addRow(index + 1, afterRow, layout, false)
            this.dropIntern(createdRow.row, event.previousContainer.data, event.previousIndex, event.currentIndex, event.previousContainer.id)
        }
    }

    dropCreateRow(event: CdkDragDrop<RowLayout>, index: number, afterRow: null | RowLayout, layout: Layout | null) {
        if (event.item.data && event.item.data.layout == true) {
            let layoutToCreate: Layout = event.item.data.id == "Repeat" ? new RepeatLayout() : event.item.data.id == "Table" ? new TableLayout() : new WebServiceLayout()
            this.addLayoutRow(index, layoutToCreate)
        } else {
            let createdRow = this.addRow(index, afterRow, layout)
            this.dropIntern(createdRow.row, event.previousContainer.data, event.previousIndex, event.currentIndex, event.previousContainer.id)
        }
    }

    selectWidget($event: Event, currentRow: RowLayout, widget: DDElement, index: number) {
        this.selectedWidget.emit({ row: currentRow, widget: widget, index: index })
        this.ddService.elementSelected = widget
        $event.stopPropagation()
    }

    selectRow($event: Event, currentRow: RowLayout, index: number) {
        this.selectedRow.emit({ row: currentRow, index: index })
        this.ddService.elementSelected = currentRow
        $event.stopPropagation()
    }

    selectLayout($event: Event, currentLayout: Layout | null, index: number) {
        if (currentLayout == null) {
            return
        }
        this.selectedLayout.emit({ row: currentLayout, index: index })
        this.ddService.elementSelected = currentLayout
        $event.stopPropagation()
    }

    selectSection() {
        this.selectedSection.emit(true)
    }

    removeWidget(currentRow: RowLayout, widget: DDElement, index: number) {
        widget.removeDependencies()
        currentRow.widgets = currentRow.widgets.filter(e => { return e != widget })
        this.removedWidget.emit({ row: currentRow, index: index, widget: widget })
        this.removeOrphanRowIfPossible(currentRow)
    }

    removeOrphanRowIfPossible(currentRow: RowLayout): boolean {
        if (currentRow.isInLayout() == false) {
            if (currentRow.widgets.length <= 0) {
                this.removeRow(currentRow)
                return true
            }
        } else if (currentRow.isInLayout()) {
            let removed = false

            for(let i = currentRow.layout!.rows.length; i > 0; i--) {
                let row = currentRow.layout!.rows[i - 1]
                if (row.widgets.length <= 0) {
                    if (!removed) removed = currentRow == row
                    this.removeRow(row)
                }
            }

            return removed
        }

        return false
    }

    rowUpLayout(layout: Layout | null, index: number) {
        if (index == 0 || layout == null) {
            return
        }
 
        let upRow = this.currentRows[index - 1]
        
        if (upRow.isInRepeat()) {
            upRow = upRow.layout!.rows[0]
        }

        this.currentRows = this.currentRows.filter(r => layout.rows.find(o => o == r) != r)
        this.currentRows.splice(index - 1, 0, ...layout.rows)
        this.movedLayout.emit({ layout: layout, row: upRow })
    }

    rowDownLayout(layout: Layout | null, index: number) {
        if (layout == null || (index + layout.rows.length >= this.currentRows.length)) {
            return
        }

        let downRow = this.currentRows[index + layout.rows.length]

        if (downRow.isInRepeat()) {
            downRow = downRow.layout!.rows[downRow.layout!.rows.length - 1]
        }

        this.currentRows = this.currentRows.filter(r => layout.rows.find(o => o == r) != r)
        this.currentRows.splice(index + 1, 0, ...layout.rows)
        this.movedLayout.emit({ layout: layout, row: downRow })
    }

    rowToUp(row: RowLayout, index: number) {
        if (index == 0) {
            return
        }

        if (row.isInRepeat() && row.widgets.length == 0) {
            this.removeRow(row)
            return
        } 

        this.dropRowIntern(this.currentRows, index, index - 1)
    }

    rowToDown(row: RowLayout, index: number) {
        if (index == this.currentRows.length - 1) {
            return
        }

        if (row.isInRepeat() && row.widgets.length == 0) {
            this.removeRow(row)
            return
        }

        this.dropRowIntern(this.currentRows, index, index + 1)
    }

    dropRow(event: CdkDragDrop<RowLayout[]>, index: number) {
        if (event.item.data.layout) {
            let upRow = this.currentRows[index]
            let layout = <Layout> this.currentRows[event.item.data.id].layout
            this.currentRows = this.currentRows.filter(r => layout.rows.find(o => o == r) != r)
            if (index > event.item.data.id) {
                index = index - layout.rows.length + 1
            } else {
                index
            }
            this.currentRows.splice(index, 0, ...layout.rows)
            this.movedLayout.emit({ layout: layout, row: upRow })
            return
        }

        this.dropRowIntern(event.container.data, event.item.data.id, index)
    }

    protected dropRowIntern(containerRows: RowLayout[], previousIndex: number, index: number) {
        let currentRow = containerRows[previousIndex]
        let rowUp = containerRows[index]
        let originLayout: Layout | null = null
        let destinyLayout: Layout | null = null
        
        if (previousIndex == index) {
            return
        }
        
        if (rowUp.isInTable() && currentRow.isInLayout()) {
            let createdRow = this.addRow(previousIndex, null, null)
            this.dropIntern(createdRow.row, currentRow, 0, 0, '')
            return
        }

        if (currentRow.isInLayout()) {
            originLayout = currentRow.layout
            currentRow.removeFromLayout()
        }

        if (rowUp.isInRepeat()) {
            destinyLayout = rowUp.layout
            currentRow.setLayoutAndAfter(rowUp.layout!, rowUp, previousIndex > index)
        }

        moveItemInArray(containerRows, previousIndex, index);
        this.movedRow.emit({ rows: containerRows, fromIndex: previousIndex, toIndex: index, rowUp: rowUp, originLayout: originLayout, destinyLayout: destinyLayout, currentRow: currentRow })

        if (rowUp.isInRepeat()) {
            rowUp.layout!.rows.forEach(r => {
                if (r.widgets.length == 0) this.removeRow(r)
            })
        }
    }


    isTool = (drag: CdkDrag, drop: CdkDropList): boolean => {

        if (drag.data && drag.data.type == 'tool') {
            return true

        }

        if (drag.data == null) {
            return true
        }

        return false
    }


    isRow = (drag: CdkDrag, drop: CdkDropList): boolean => {
        if (drag.data && drag.data.type == 'row') {
            return true

        }
        return false
    }

    isRowNotLayout = (drag: CdkDrag, drop: CdkDropList): boolean => {
        if (drag.data && drag.data.type == 'row' && drag.data.layout == null) {
            return true
        }
        return false
    }

    isNotRow = (drag: CdkDrag, drop: CdkDropList): boolean => {

        if (drag.data && (drag.data.type == 'row' || (drag.data.type == 'tool' && drag.data.layout == true))) {
            return false
        }

        if (this.ddService.ddElement) {
            if (drop.data.thereIsAvailableSlots != null && drop.data.thereIsAvailableSlots(this.ddService.ddElement.slotWeight())) {
                return true
            }
            return false
        }

        return true
    }

    drop(event: CdkDragDrop<RowLayout>) {
        this.dropIntern(event.container.data, event.previousContainer.data, event.previousIndex, event.currentIndex, event.previousContainer.id)
    }

    dropIntern(container: RowLayout, previousContainer: RowLayout, previousIndex: number, currentIndex: number, previousContainerId: string) {
        if (previousContainer === container) {
            moveItemInArray(container.widgets, previousIndex, currentIndex);
            this.movedWidget.emit({ row: container, fromIndex: previousIndex, toIndex: currentIndex })
            return
        }

        if (container.thereIsAvailableSlots(previousContainer.widgets[previousIndex].slotWeight()) == false) {
            return
        }

        if (previousContainerId == 'tools') {
            this.createWidget(previousContainer, container, previousIndex, currentIndex);
            return
        }

        transferArrayItem(
            previousContainer.widgets,
            container.widgets,
            previousIndex,
            currentIndex,
        );

        this.transferredWidget.emit({
            sourceRow: previousContainer,
            sourceIndex: previousIndex,
            destinyRow: container,
            destinyIndex: currentIndex
        })

        let hasBeenRemoved = this.removeOrphanRowIfPossible(previousContainer) // Remove orfes on old row //
        this.removeOrphanRowIfPossible(container) // Remove orfes on new row //

        if (hasBeenRemoved == false) {
            this.recordOnHistory.emit(true)
        }
    }

    public createWidget(previousContainer: RowLayout, container: RowLayout, previousIndex: number, currentIndex: number, clone: boolean = true): CreatedWidget {
        copyArrayItem(
            previousContainer.widgets,
            container.widgets,
            previousIndex,
            currentIndex
        );

        if (clone) {
            container.widgets[currentIndex] = container.widgets[currentIndex].clone();
        }

        let result = { row: container, toIndex: currentIndex, widget: container.widgets[currentIndex] }
        this.createdWidget.emit(result);
        return result
    }


    moveWidget(event: RowLayout, previousIndex: number, currentIndex: number) {
        moveItemInArray(event.widgets, previousIndex, currentIndex);
        this.movedWidget.emit({ row: event, fromIndex: previousIndex, toIndex: currentIndex })
        return
    }

    onMouseLayout(layout: Layout | null) {
        this.hoverRowLayout = layout
    }

    isInHoverLayout(currentRow: RowLayout): boolean {
        return currentRow.isInLayout() && (this.hoverRowLayout == currentRow.layout)
    }

    moveScroll(id: string) {
        setTimeout(() => {
            let element = document.getElementById('editor-' + id)
            if (element == null) {
                return
            }
            element.scrollIntoView({
                behavior: "auto",
                block: "center",
                inline: 'start'
            });
        }, 150)
    }

    doRequestAddTool(hasLayout: boolean, index: number, afterRow: null | RowLayout, layout: Layout | null) {
        let requestedAddTool = { index: index, hasLayout: hasLayout, afterRow: afterRow, layout: layout }
        this.requestAddTool.emit(requestedAddTool)
    }

    isTableLayout(layout: Layout | null): boolean {
        if(layout && layout.metadata.type == 'TABLE_LAYOUT') return true
        return false
    }

}
